import React, {
  useCallback, useEffect, useMemo, useState, forwardRef, ForwardedRef, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, NavLink as RouterNavLink } from 'react-router-dom';
import { Box, Grid } from '@material-ui/core';

import {
  completeTaskRequest,
  completeTaskClean,
  getTaskRequest,
  getTaskClean,
  selectUserTaskError,
  selectUserTaskIsLoading,
  selectUserTask,
  selectUserTaskFormSubmissionData,
  selectUserTaskValidationErrors,
  selectUserTaskForm,
  selectSignUserTaskValidationErrors,
  signTaskClean,
  selectFormVariables,
  signTaskRequest,
  saveTaskRequest,
} from 'store/userTask';
import { FormEvalContext, FormSubmission } from 'types/form';
import { ROUTES } from 'constants/routes';
import StandardLayout from 'components/Layouts/Standard';
import { taskSuspendedErrorProps } from 'constants/errorProps';
import FlashMessage from '#web-components/components/FlashMessage';
import { X_PATH } from 'constants/xPath';
import { selectCurrentUserInfo } from 'store/currentUser';
import { NAVIGATION_CODE } from '#web-components/components/Form/constants';
import {
  ButtonComponent,
  FileComponent, FormComponent, FORMIO_EVENT,
} from '#web-components/components/Form/types';
import {
  findComponents,
  filterComponents,
  isFileComponent,
  isNavigationButton,
  prepareFileSubmission,
  isNotCustomButton,
  hasRequiredFields,
} from '#web-components/utils';
import { Formio } from '#web-components/exports/formio';
import SignatureWidget from '#web-components/components/SignatureWidget';
import Form, {
  FormioModule,
} from '#web-components/components/Form';
import { assignMultipleRefs } from '#web-components/utils/common';
import RouterPrompt from '#shared/utils/RouterPrompt';

const validationMessagesProps = {
  flashMessagesProps: {
    xpathConfig: {
      messageBlock: X_PATH.validationMessage,
      titleBlock: X_PATH.validationMessageTitle,
    },
  },
};
const SIGN_WIDGET_XPATH_CONFIG = {
  changeKeyButton: X_PATH.changeKeyButton,
  signButton: X_PATH.signButton,
};

Formio.use(FormioModule);
Formio.setBaseUrl(ENVIRONMENT_VARIABLES?.apiUrl || window.location.origin);

interface Params {
  taskId: string;
}

function TaskPage(props: unknown, forwardedRef: ForwardedRef<Formio>) {
  const formRef = useRef<Formio>();
  const { t, i18n } = useTranslation('pages');
  const dispatch = useDispatch();
  const params = useParams<Params>();
  const form = useSelector(selectUserTaskForm);
  const userTask = useSelector(selectUserTask);
  const isSignNeeded = userTask?.esign;

  const preparedComponents = useMemo(() => {
    if (isSignNeeded) {
      return filterComponents<ButtonComponent>(
        form?.components || [],
        (component) => !(component?.type === 'button' && isNotCustomButton(component.action)),
      );
    }
    return form?.components || [];
  }, [isSignNeeded, form?.components]);

  const requiredFieldsPresent = useMemo(() => hasRequiredFields(preparedComponents), [preparedComponents]);

  const taskSubmissionData = useSelector(selectUserTaskFormSubmissionData);
  const formVariables = useSelector(selectFormVariables);

  const preparedTaskData = useMemo(() => {
    const { data } = prepareFileSubmission(preparedComponents, { data: formVariables });
    return data;
  }, [formVariables, preparedComponents]);
  const isLoading = useSelector(selectUserTaskIsLoading);
  const userInfo = useSelector(selectCurrentUserInfo);
  const error = useSelector(selectUserTaskError);
  const validationErrors = useSelector(selectUserTaskValidationErrors);
  const signValidationErrors = useSelector(selectSignUserTaskValidationErrors);
  const suspendedError = userTask?.suspended ? taskSuspendedErrorProps : undefined;
  const [signWidgetLoading, setSignWidgetLoading] = useState(false);
  const [isFormDirty, setFormDirty] = useState(false);

  const formEvalContext: FormEvalContext = useMemo(() => ({
    formVariables: userTask?.formVariables || null,
    currentUser: userInfo,
  }), [userInfo, userTask?.formVariables]);

  const completeTask = useCallback((formData: FormSubmission) => {
    dispatch(completeTaskRequest({
      taskId: params.taskId,
      processInstanceId: userTask?.processInstanceId,
      formData,
    }));
  }, [dispatch, params.taskId, userTask?.processInstanceId]);

  const handleNavigation = useCallback((actionCode: string) => {
    dispatch(signTaskClean());
    completeTask({
      data: {
        ...preparedTaskData,
        [NAVIGATION_CODE]: actionCode,
      },
    });
  }, [completeTask, dispatch, preparedTaskData]);

  const handleSubmit = useCallback((submission: FormSubmission) => {
    completeTask(submission);
  }, [completeTask]);

  const handleSignText = useCallback((signature: string) => {
    dispatch(signTaskRequest({
      taskId: params.taskId,
      processInstanceId: userTask?.processInstanceId,
      data: {
        data: preparedTaskData,
        signature,
      },
    }));
  }, [dispatch, params.taskId, preparedTaskData, userTask?.processInstanceId]);

  const fileUrlTransformer = useCallback((component: FormComponent) => {
    if (!isFileComponent(component.type)) {
      return component;
    }
    const newUrl = `
          ${(component as FileComponent).url}/${userTask?.rootProcessInstanceId}/${userTask?.id}/${component.key}`;
    return {
      ...component,
      url: newUrl,
    };
  }, [userTask?.id, userTask?.rootProcessInstanceId]);

  useEffect(() => {
    dispatch(getTaskRequest(params.taskId));
  }, [dispatch, params.taskId]);

  useEffect(() => {
    return function cleanup() {
      dispatch(completeTaskClean());
      dispatch(getTaskClean());
      dispatch(signTaskClean());
    };
  }, [dispatch]);

  useEffect(() => {
    if (validationErrors.length > 0 || signValidationErrors.length > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [validationErrors, signValidationErrors]);

  const handleClickSave = useCallback((data: Record<string, unknown>) => {
    if (formRef.current) {
      const { formio } = formRef.current;
      const isValid = formio.checkValidity(formio.data, true);
      if (isValid) {
        dispatch(saveTaskRequest({
          taskId: params.taskId,
          data,
        }));
      }
    }
  }, [dispatch, params.taskId]);

  const handleCustomEvent = useCallback((data: { type: string, data: FormSubmission; }) => {
    const { type, data: formData } = data;
    if (type === FORMIO_EVENT.NAVIGATION) {
      completeTask(formData);
    }
    if (type === FORMIO_EVENT.SAVE) {
      handleClickSave(formData.storageData || {});
    }
  }, [completeTask, handleClickSave]);

  const handleDirtyChange = useCallback((status) => {
    setFormDirty(status);
  }, []);

  const MemoForm = useMemo(() => {
    return (
      <Form
        formRef={(node) => assignMultipleRefs(node, formRef, forwardedRef)}
        components={preparedComponents}
        onSubmit={handleSubmit}
        onCustomEvent={handleCustomEvent}
        submissionData={taskSubmissionData}
        validationErrors={validationErrors.concat(signValidationErrors)}
        readOnly={isSignNeeded}
        language={i18n.language}
        validationMessagesProps={validationMessagesProps}
        evalContext={formEvalContext}
        componentsTransformer={fileUrlTransformer}
        onDirtyChange={handleDirtyChange}
        blackList={ENVIRONMENT_VARIABLES.emailBlacklist || []}
        fileMaxSize={ENVIRONMENT_VARIABLES.digitalDocumentsMaxFileSize}
        fileMaxTotalSize={ENVIRONMENT_VARIABLES.digitalDocumentsMaxTotalFileSize}
      />
    );
  }, [
    fileUrlTransformer,
    formEvalContext,
    forwardedRef,
    handleCustomEvent,
    handleDirtyChange,
    handleSubmit,
    i18n.language,
    isSignNeeded,
    preparedComponents,
    signValidationErrors,
    taskSubmissionData,
    validationErrors,
  ]);

  return (
    <StandardLayout
      title={form?.title || t('task.title')}
      titleVariant="h2"
      isLoading={isLoading || signWidgetLoading}
      backLink={{
        path: ROUTES.PROCESS_INSTANCE_LIST,
        title: t('task.backLinkCaption'),
        RouterNavLink,
      }}
      error={error || suspendedError}
    >
      <RouterPrompt
        enabled={isFormDirty}
        baseName=""
        okText={t('components:routerPrompt.okText')}
        cancelText={t('components:routerPrompt.cancelText')}
        text={t('components:routerPrompt.text')}
        title={t('components:routerPrompt.title')}
      />
      <Grid container>
        <Grid item sm={8}>
          {
            !isSignNeeded && requiredFieldsPresent && (
              <Box mb={4}>
                <FlashMessage
                  status="info"
                  message={t('domains:form.messages.mandatoryFields')}
                />
              </Box>
            )
          }
        </Grid>
      </Grid>
      {MemoForm}
      <Grid container>
        <Grid item sm={8}>
          {isSignNeeded && (
            <SignatureWidget
              signWidgetUri={ENVIRONMENT_VARIABLES.citizenPortalSignWidgetUrl}
              signWidgetHeight={ENVIRONMENT_VARIABLES.citizenPortalSignWidgetHeight}
              changeKeyButtonCaption={t('components:signWidget.changeKeyButton')}
              signButtonCaption={t('components:signWidget.signButton')}
              signDataTitle={t('components:signWidget.signDataTitle')}
              readKeyTitle={t('components:signWidget.readKeyTitle')}
              organizationCaption={t('components:signWidget.organization')}
              rnokppCaption={t('components:signWidget.rnokpp')}
              edrpouCaption={t('components:signWidget.edrpou')}
              xpathConfig={SIGN_WIDGET_XPATH_CONFIG}
              dataToSign={preparedTaskData}
              onSignText={handleSignText}
              onLoadingChange={setSignWidgetLoading}
              navigationButton={findComponents(
                form?.components || [],
                (component) => isNavigationButton(component.action),
              )}
              onNavigation={handleNavigation}
              errorButtonCaption={t('components:signWidget.errorButtonText')}
              qrCode={{
                title: t('components:signWidget.qrCode.title'),
                description: t('components:signWidget.qrCode.description'),
                timerText: t('components:signWidget.qrCode.timerText'),
                errorMessage: t('components:signWidget.qrCode.errorMessage'),
              }}
              infoMessage={(
                <Box mb={6} mt={8}>
                  <FlashMessage
                    status="info"
                    message={t('components:signWidget.sideBarContent')}
                  />
                </Box>
              )}
            />
          )}
        </Grid>
      </Grid>
    </StandardLayout>
  );
}

export default forwardRef(TaskPage);
